import React, { useState, useEffect, useMemo } from 'react'
import { getAllSupportChats, subscribeToSupportChats, subscribeToSupportChatsByStatus } from '../firebase/supportChats'
import { useAuth } from '../context/authContext'
import Pagination from '../components/commons/Pagination'
import DialogSettlement from '../components/dialogs/DialogSettlement'
import {  getIncomeStatistics, getAllIncomes, getPlatformBalance } from '../firebase/incomes'
import { Button } from '@headlessui/react'

const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

const getStatusColor = (status) => {
    switch (status) {
        case 'active': return 'bg-green-500'
        case 'pending': return 'bg-yellow-500'
        case 'resolved': return 'bg-blue-500'
        case 'closed': return 'bg-gray-500'
        default: return 'bg-gray-500'
    }
}

const IncomeItem = ({ income }) => {
    return (
        <tr key={income.id} className='h-[70px] hover:bg-[#2F2F2F] p-2'>
            <td className="py-4">{income.amount}</td>
            <td className="py-4">{income.type}</td>
            <td className="py-4">{income.description}</td>
            <td className="py-4">{income.user}</td>
            <td className="py-4">{formatDate(income.created_at)}</td>
            <td className="py-4">{income.settled ? 'Yes' : 'No'}</td>
            <td className="py-4">{income.settled_at ? formatDate(income.settled_at) : 'N/A'}</td>
            <td className="py-4">{income.settle_by || 'N/A'}</td>
        </tr>
    )
}

const IncomeManagement = () => {
    const [incomes, setIncomes] = useState([])
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [platformBalance, setPlatformBalance] = useState({ available: 0, pending: 0, error: null, loading: true })
    const [reloadPlatformBalance, setReloadPlatformBalance] = useState(new Date())
    const itemsPerPage = 8
    const [totalCount, setTotalCount] = useState(0);
    const [showSettleDialog, setShowSettleDialog] = useState(false)
    const [incomeStatistics, setIncomeStatistics] = useState({
        totalIncome: 0,
        settledIncome: 0,
        settleableIncome: 0,
        pendingIncome: 0,
        unsettledIncome: 0
    })
    const [lastUpdatedAt, setLastupdatedAt] = useState(Date.now())
    const { user } = useAuth()

    useEffect(()=>{
        const t = setInterval(() => setLastupdatedAt(Date.now()), 5000) // Refresh every 30 seconds
        return () => clearInterval(t)
    }, [])
    useEffect(()=>{
        getIncomeStatistics()
            .then(stats => setIncomeStatistics(stats))
            .catch(error => console.error('Error fetching income statistics:', error)); 
    }, [lastUpdatedAt])
    
    useEffect(() => {
        setPlatformBalance({ available: 0, pending: 0, error: null, loading: true })
        getPlatformBalance( user.uid )
            .then(balance => setPlatformBalance(balance))
            .catch(error => setPlatformBalance({ available: 0, pending: 0, error: error.message || 'Failed to fetch platform balance' }));
    }, [reloadPlatformBalance, user.uid])

    useEffect(() => {
        setLoading(true)
        getAllIncomes(currentPage, itemsPerPage)
            .then(({ allIncomes }) => {
                setIncomes(allIncomes)
                setTotalCount(allIncomes.length) // Assuming allIncomes is an array of income objects
                setLoading(false)
            })
            .catch(error => {
                console.error('Error fetching incomes:', error)
                setLoading(false)
            })
        }, []);
    
    const currentPageIncomes = useMemo(() => 
        incomes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    , [incomes, currentPage, itemsPerPage])

    const fields = ["Amount", "Type", "Description", "User", "Created At", "Settled", "Settled At", "Settled By"];

    return (
        <div className="min-h-full bg-[#0F0F0F] text-white">
            <div className="px-10 py-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <h1 className="text-primary text-[36px]">Income Manager</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="px-6 py-2 bg-[#1F1F1F] rounded-[33px] text-[#666] text-center">
                            {incomes.length} Incomes
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <Button onClick={() => setShowSettleDialog(true)} className="px-4 py-2 bg-primary rounded-[33px] text-white">
                            Settle Incomes
                        </Button>
                    </div>
                </div>

                <div className="mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="bg-[#1F1F1F] p-4 rounded-xl shadow-sm">
                            <div className="text-[#999] text-sm">Available Balance</div>
                            {
                                platformBalance.loading ? (
                                    <div className="text-gray-600 text-sm mt-1">Loading...</div>
                                ) : (
                                    platformBalance.error ? (
                                        <div className="text-red-500 text-sm mt-1">{platformBalance.error}</div>
                                    ) : (
                                        <>      
                                            <div className="text-white text-2xl font-bold mt-2">${platformBalance.available.toLocaleString()}</div>
                                            <div className="text-[#888] text-xs mt-1">${platformBalance.pending.toLocaleString()} pending</div>
                                        </> 
                                    )
                                )
                            } 
                        </div>
                        <div className="bg-[#1F1F1F] p-4 rounded-xl shadow-sm">
                            <div className="text-[#999] text-sm">Total Income</div>
                            <div className="text-white text-2xl font-bold mt-2">${incomeStatistics.totalIncome.toLocaleString()}</div>
                            <div className="text-[#888] text-xs mt-1">All-time recorded income</div>
                        </div>

                        <div className="bg-[#1F1F1F] p-4 rounded-xl shadow-sm">
                            <div className="text-[#999] text-sm">Settled Income</div>
                            <div className="text-white text-2xl font-bold mt-2">${incomeStatistics.settledIncome.toLocaleString()}</div>
                            <div className="text-[#888] text-xs mt-1">Already settled to users</div>
                        </div>

                        <div className="bg-[#1F1F1F] p-4 rounded-xl shadow-sm">
                            <div className="text-[#999] text-sm">Settleable</div>
                            <div className="text-white text-2xl font-bold mt-2">${incomeStatistics.settleableIncome.toLocaleString()}</div>
                            <div className="text-[#888] text-xs mt-1">Ready to be settled</div>
                        </div>

                        <div className="bg-[#1F1F1F] p-4 rounded-xl shadow-sm">
                            <div className="text-[#999] text-sm">Pending</div>
                            <div className="text-white text-2xl font-bold mt-2">${incomeStatistics.pendingIncome.toLocaleString()}</div>
                            <div className="text-[#888] text-xs mt-1">Under review / processing</div>
                        </div>

                        <div className="bg-[#1F1F1F] p-4 rounded-xl shadow-sm">
                            <div className="text-[#999] text-sm">Unsettled</div>
                            <div className="text-white text-2xl font-bold mt-2">${incomeStatistics.unsettledIncome.toLocaleString()}</div>
                            <div className="text-[#888] text-xs mt-1">Not eligible for settlement</div>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center">
                            <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-8 h-8 animate-spin mb-4" />
                            <p className="text-white text-lg">Loading incomes...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="my-10">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-[#2F2F2F]">
                                        {fields.map((field, index) => (
                                            <th key={index} className="text-left py-4 text-[#666] font-medium">
                                                {field}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPageIncomes && currentPageIncomes.map((income) => (
                                        <IncomeItem
                                            key={income.id}
                                            income={income}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        <div className="mt-auto">
                            <Pagination 
                                itemsPerPage={itemsPerPage} 
                                totalItems={totalCount} 
                                onPageChange={(v) => setCurrentPage(v)} 
                            />
                        </div>
                    </>
                )}
            </div>

            {showSettleDialog && (
                <DialogSettlement
                    open={showSettleDialog}
                    onClose={(success = false) => {
                        setShowSettleDialog(false)
                        if(success) {
                            setLastupdatedAt(Date.now()) // Refresh data after settlement
                            setReloadPlatformBalance(new Date()) // Reload platform balance
                        }
                    }}
                    />
            )}
        </div>
    )
}

export default IncomeManagement 